
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Coffee, Bell } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type BreakTimerModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  duration?: number; // duration in minutes
};

export default function BreakTimerModal({ 
  isOpen, 
  onOpenChange, 
  duration = 5 
}: BreakTimerModalProps) {
  const { toast } = useToast();
  const [timeRemaining, setTimeRemaining] = useState(duration * 60); // in seconds
  const [isRunning, setIsRunning] = useState(false);
  
  // Reset timer when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeRemaining(duration * 60);
      setIsRunning(true);
    } else {
      setIsRunning(false);
    }
  }, [isOpen, duration]);
  
  // Timer logic
  useEffect(() => {
    let interval: number | undefined;
    
    if (isRunning && timeRemaining > 0) {
      interval = window.setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setIsRunning(false);
            toast({
              title: "Break time's up!",
              description: "Your break has ended. Time to get back to work!",
            });
            // Show notification if browser supports it
            if ("Notification" in window && Notification.permission === "granted") {
              new Notification("Break time's up!", {
                body: "Your break has ended. Time to get back to work!",
                icon: "/favicon.ico"
              });
            }
            window.clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (interval) window.clearInterval(interval);
    };
  }, [isRunning, timeRemaining, toast]);
  
  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Calculate progress percentage
  const progressPercentage = (timeRemaining / (duration * 60)) * 100;
  
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coffee className="h-5 w-5 text-primary" />
            <span>Take a Break</span>
          </DialogTitle>
          <DialogDescription>
            Time to recharge! Step away from your screen and relax.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-6 flex flex-col items-center justify-center">
          <div className="text-4xl font-bold mb-4">{formatTime(timeRemaining)}</div>
          <Progress 
            value={progressPercentage} 
            className="w-full h-2 mb-4" 
          />
          <p className="text-sm text-muted-foreground text-center">
            Taking breaks improves productivity and mental wellbeing.
          </p>
        </div>
        
        <DialogFooter className="sm:justify-between">
          <div className="hidden sm:block">
            <Button 
              variant="outline" 
              onClick={() => {
                // Request notification permission if not granted
                if ("Notification" in window && Notification.permission !== "granted") {
                  Notification.requestPermission();
                }
              }}
              className="flex items-center gap-2"
              type="button"
            >
              <Bell className="h-4 w-4" />
              <span>Enable Notifications</span>
            </Button>
          </div>
          <Button onClick={() => onOpenChange(false)} type="button">
            End Break Early
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
